import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, AlertTriangle } from 'lucide-react';
import { useAuth } from '@/contexts/useAuth';
import { createAdminApiClient } from '@/lib/admin-api-client';
import type { AdminUser } from '@/lib/admin-types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';

const PAGE_SIZE = 20;

export default function UserListPage() {
  const { idToken } = useAuth();
  const navigate = useNavigate();

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pageToken, setPageToken] = useState<string | undefined>();
  const [prevTokens, setPrevTokens] = useState<string[]>([]);
  const [nextToken, setNextToken] = useState<string | undefined>();
  const [emailFilter, setEmailFilter] = useState('');
  const [debouncedEmail, setDebouncedEmail] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const client = useMemo(
    () => createAdminApiClient(
      import.meta.env.VITE_API_GATEWAY_BASE_URL,
      () => idToken ?? '',
    ),
    [idToken],
  );

  const fetchUsers = useCallback(
    async (token?: string, email?: string) => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await client.listUsers({
          limit: PAGE_SIZE,
          paginationToken: token,
          emailFilter: email || undefined,
        });
        setUsers(res.users);
        setNextToken(res.nextToken);
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : 'Failed to load users';
        setError(msg);
        console.error('[UserListPage] GET /users', err);
      } finally {
        setIsLoading(false);
      }
    },
    [client],
  );

  useEffect(() => {
    fetchUsers(pageToken, debouncedEmail);
  }, [pageToken, debouncedEmail, fetchUsers]);

  // Debounced email search
  const handleSearchChange = (value: string) => {
    setEmailFilter(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPageToken(undefined);
      setPrevTokens([]);
      setDebouncedEmail(value);
    }, 300);
  };

  const handleNextPage = () => {
    if (!nextToken) return;
    setPrevTokens((prev) => [...prev, pageToken ?? '']);
    setPageToken(nextToken);
  };

  const handlePrevPage = () => {
    const prev = [...prevTokens];
    const token = prev.pop();
    setPrevTokens(prev);
    setPageToken(token || undefined);
  };

  return (
    <section aria-label="User Management">
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-2xl font-semibold">Users</h2>
        <Button onClick={() => navigate('/admin/users/new')}>
          <Plus className="size-4" aria-hidden="true" />
          Create User
        </Button>
      </div>

      <div className="mb-4 max-w-sm">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <Input
            placeholder="Search by email..."
            value={emailFilter}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-9"
            aria-label="Filter users by email"
          />
        </div>
      </div>

      {error && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/5 p-4 text-destructive" role="alert">
          <AlertTriangle className="size-5 shrink-0" aria-hidden="true" />
          <p className="flex-1">{error}</p>
          <Button variant="outline" size="sm" onClick={() => fetchUsers(pageToken, debouncedEmail)}>
            Retry
          </Button>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="px-4 py-3 text-left font-medium">Email</th>
              <th className="px-4 py-3 text-left font-medium">Role</th>
              <th className="px-4 py-3 text-left font-medium">Status</th>
              <th className="px-4 py-3 text-left font-medium">Created</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i} className="border-b">
                  <td className="px-4 py-3"><Skeleton className="h-4 w-48" /></td>
                  <td className="px-4 py-3"><Skeleton className="h-4 w-20" /></td>
                  <td className="px-4 py-3"><Skeleton className="h-4 w-16" /></td>
                  <td className="px-4 py-3"><Skeleton className="h-4 w-24" /></td>
                </tr>
              ))
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">
                  No users found.
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <tr
                  key={user.userId}
                  className="cursor-pointer border-b transition-colors hover:bg-muted/50"
                  onClick={() => navigate(`/admin/users/${user.userId}`)}
                  role="link"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') navigate(`/admin/users/${user.userId}`);
                  }}
                >
                  <td className="px-4 py-3 font-medium">{user.email}</td>
                  <td className="px-4 py-3 capitalize">{user.role}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                        user.enabled
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                      }`}
                    >
                      {user.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {new Date(user.createdAt).toLocaleDateString()}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {!isLoading && (prevTokens.length > 0 || nextToken) && (
        <div className="mt-4 flex justify-between">
          <Button
            variant="outline"
            size="sm"
            disabled={prevTokens.length === 0}
            onClick={handlePrevPage}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!nextToken}
            onClick={handleNextPage}
          >
            Next
          </Button>
        </div>
      )}
    </section>
  );
}
